import { useCallback, useMemo, useSyncExternalStore } from 'react';
import {
  resolveDefaultLocationId,
  useSwitcherLocations,
} from '@/hooks/useFoundingPricing';
import { ALL_LOCATIONS_VALUE } from '@/components/ui/tee-location-select';
import { LocationSummaryDto } from '@/types/location';

/**
 * Single shared localStorage key for the box-os location filter selector.
 *
 * '' (ALL_LOCATIONS_VALUE) means the user explicitly chose "All locations".
 * null (no key present) means the user has never chosen anything yet.
 *
 * When the stored value can't be resolved against the live switcher list
 * (stale id, inactive location, list not loaded yet on an allowAll=false
 * page) callers fall back to a computed default — but that fallback is
 * NEVER written back to storage. Only an explicit `setLocationId` call
 * persists a choice, so a page that can't show "All" (e.g. Bay Utilization)
 * never clobbers an "All" choice made on another page.
 */
export const SELECTED_LOCATION_STORAGE_KEY = 'box-os-selected-location';

/** Prior per-page persistence islands folded into the shared store. */
export const LEGACY_LOCATION_KEYS = [
  'calendarSelectedLocationId',
  'gameplan.selectedLocationId',
];

// Module-level store so every hook instance across every page observes the
// same selected location (mirrors useInboxLocation.ts's pattern).
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

/**
 * Reads the shared stored location id.
 * - null: user never chose (and no legacy key to migrate).
 * - '': user chose "All locations".
 * - otherwise: the chosen location id.
 *
 * One-time migration: if the new key is absent, falls back to the first
 * present legacy key's value, writes it to the new key, and removes all
 * legacy keys.
 */
export function readStoredLocationId(): string | null {
  try {
    const current = window.localStorage.getItem(SELECTED_LOCATION_STORAGE_KEY);
    if (current !== null) return current;

    for (const legacyKey of LEGACY_LOCATION_KEYS) {
      const legacyValue = window.localStorage.getItem(legacyKey);
      if (legacyValue !== null) {
        try {
          window.localStorage.setItem(SELECTED_LOCATION_STORAGE_KEY, legacyValue);
        } catch {
          // storage unavailable: still return the migrated value for this tick
        }
        for (const keyToRemove of LEGACY_LOCATION_KEYS) {
          try {
            window.localStorage.removeItem(keyToRemove);
          } catch {
            // ignore
          }
        }
        return legacyValue;
      }
    }

    return null;
  } catch {
    return null;
  }
}

/** Persists the chosen location id ('' is a valid explicit choice: All). */
export function writeStoredLocationId(id: string): void {
  try {
    window.localStorage.setItem(SELECTED_LOCATION_STORAGE_KEY, id);
  } catch {
    // storage unavailable: selection stays in-memory only for this tick
  }
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === SELECTED_LOCATION_STORAGE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Raw access to the shared store without fetching the switcher location
 * list. For pages that validate the id against their own location list
 * (e.g. Calendar validates against its own calendar locations by _id).
 */
export function useStoredLocationId(): [string | null, (id: string) => void] {
  const storedLocationId = useSyncExternalStore(subscribe, readStoredLocationId, () => null);
  const setLocationId = useCallback((id: string) => writeStoredLocationId(id), []);
  return [storedLocationId, setLocationId];
}

export interface UseSelectedLocationOptions {
  /** Whether this page's selector allows "All locations" (''). Default false. */
  allowAll?: boolean;
}

export interface UseSelectedLocationResult {
  locationId: string;
  setLocationId: (id: string) => void;
  locations: LocationSummaryDto[];
  isLoading: boolean;
}

/**
 * Shared, persisted "selected location" filter for box-os pages that render
 * a filter-style TeeLocationSelect. Backed by the switcher location list
 * (`useSwitcherLocations`, shared React Query cache) and the single
 * `box-os-selected-location` localStorage key.
 */
export function useSelectedLocation(
  options: UseSelectedLocationOptions = {},
): UseSelectedLocationResult {
  const { allowAll = false } = options;
  const storedLocationId = useSyncExternalStore(subscribe, readStoredLocationId, () => null);
  const { data, isLoading } = useSwitcherLocations();
  const locations = data ?? [];
  const activeLocations = useMemo(
    () => locations.filter((l) => l.status === 'ACTIVE'),
    [locations],
  );

  const locationId = useMemo(() => {
    if (isLoading) {
      // Optimistic: avoid a flash while the list loads.
      return storedLocationId && storedLocationId !== '' ? storedLocationId : '';
    }

    const storedIsValid =
      !!storedLocationId &&
      storedLocationId !== '' &&
      activeLocations.some((l) => l.id === storedLocationId);
    if (storedIsValid) return storedLocationId as string;

    // Covers: stored '' (explicit All), stored null (never chosen), and a
    // stale/inactive stored id — all resolve to "All" on allowAll pages.
    if (allowAll) return ALL_LOCATIONS_VALUE;

    return resolveDefaultLocationId(activeLocations) ?? '';
  }, [isLoading, storedLocationId, activeLocations, allowAll]);

  const setLocationId = useCallback((id: string) => writeStoredLocationId(id), []);

  return { locationId, setLocationId, locations, isLoading };
}
